import { Tooltip } from "@material-ui/core";
import React from "react";
import { FiUser } from "react-icons/fi";

import useOverflowCheck from "../../hooks/useOverflowCheck";
import ModalAutoRenew, { steps } from "../../ModalAutoRenew/ModalAutoRenew";
import { listOfSubscriptions } from "../../../../common/envConfig";
import { getStatusClass, getStatusText } from "../../utils/status";
import { Toggle } from "../../../../components/shared/Toggle/Toggle";

const StudentCreditsManageTableRow = ({ student, user, onOpenModal }) => {
  const studentFullName = `${student?.firstName} ${student?.lastName}`;
  const rawDaysLeft = Number(student?.daysLeft);
  const daysLeft = Math.ceil(rawDaysLeft);
  const subscriptionType = student?.subscriptionType ?? null;
  const isFreeStudent = Boolean(subscriptionType?.includes("free"));

  // ----- Subscription derived state -----
  const sub = student?.subscription ?? null;
  const hasSubId = Boolean(sub?.id);
  const isAutoRenewOn = sub?.autoRenewal === true;
  const isSameCoach = sub?.coachId === user?.id;
  const isCredits = sub?.platform === "Credits";
  const isStripe = sub?.platform === "Stripe"; // if still used elsewhere
  const isCancelled = sub?.status === "canceled";

  const isPlayerPlus =
    subscriptionType === listOfSubscriptions.PLAYER_PLUS_MONTHLY ||
    subscriptionType === listOfSubscriptions.PLAYER_PLUS_ANNUALLY;

  const isFreeSubscription =
    subscriptionType == null ||
    subscriptionType === listOfSubscriptions.STUDENT_FREE;

  const is3DPlayer =
    subscriptionType === listOfSubscriptions.STUDENT_LITE_MONTHLY ||
    subscriptionType === listOfSubscriptions.PLAYER_PLUS_ANNUALLY;

  const isUpgradable3DPlayer = is3DPlayer && isCredits && !isAutoRenewOn;

  // ----- Days-left "n/a" logic (your clarified rules) -----
  // true if: free student OR no subscription OR (no coachId AND endDate is expired)
  const endMs = sub?.endDate != null ? Number(sub.endDate) : NaN;
  const isExpired = Number.isFinite(endMs) && endMs < Date.now();
  const hasCoachId = sub ? "coachId" in sub : false;

  const daysLeftIsNA = isFreeStudent || !sub || (!hasCoachId && isExpired);

  const canEnableByDays = !Number.isFinite(rawDaysLeft) || rawDaysLeft >= 1;

  const canUseAutoRenewButton =
    !isPlayerPlus &&
    hasSubId &&
    isCredits &&
    ((isAutoRenewOn && isSameCoach) || // allow stopping if ON and same coach
      (!isAutoRenewOn && canEnableByDays)); // allow enabling only if >= 1 day left

  // Tooltip text mirrors the same rules used above
  const getAutoRenewTooltip = () => {
    if (isPlayerPlus) {
      return (
        <div className="text-white text-sm">
          Player Plus subscription cannot be auto-renewed
        </div>
      );
    }
    if (!hasSubId || (hasSubId && !isCredits)) {
      return (
        <div className="text-white text-sm">
          Apply credits to this student before enabling auto-renew.
        </div>
      );
    }
    if (!isAutoRenewOn && Number.isFinite(rawDaysLeft) && rawDaysLeft < 1) {
      return (
        <div className="text-white text-sm">
          Cannot enable auto-renew with less than 1 day remaining.
        </div>
      );
    }
    if (!canUseAutoRenewButton && isAutoRenewOn && !isSameCoach) {
      return (
        <div className="text-white text-sm">
          Disabled because auto-renewal was changed by another user
        </div>
      );
    }
    return "";
  };

  const isCreditButtonEnabled =
    isFreeSubscription || isUpgradable3DPlayer || isExpired;

  const onToggleAutoRenewal = () => {
    if (sub?.autoRenewal) {
      onOpenModal(student, steps.AUTO_RENEW_STOP);
    } else {
      onOpenModal(student, steps.AUTO_RENEW);
    }
  };

  const { textRef, isOverflowing } = useOverflowCheck({
    dependency: studentFullName,
  });

  const { textRef: textRefEmail, isOverflowing: isOverflowingEmail } =
    useOverflowCheck({
      dependency: student?.email,
    });

  return (
    <>
      <tr
        key={student?.id}
        className="border-x-0 border-b-0 border-t border-gray200"
      >
        <td className="min-w-[180px] max-w-[180px] w-[180px] px-3 py-4">
          <div className="flex items-center gap-3">
            {student?.picture ? (
              <img
                src={student?.picture}
                alt={student?.firstName}
                className="min-w-10 min-h-10 w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="min-w-10 min-h-10 w-10 h-10 rounded-full flex items-center justify-center bg-gray100">
                <FiUser className="text-gray600 w-6 h-6" />
              </div>
            )}

            <Tooltip
              enterTouchDelay={0}
              title={
                isOverflowing ? (
                  <div className="text-white text-sm">
                    {student?.firstName} {student?.lastName}
                  </div>
                ) : (
                  ""
                )
              }
              placement="top"
              arrow
            >
              <div
                ref={textRef}
                className="font-semibold text-xs text-gray900 text-nowrap text-ellipsis overflow-hidden"
              >
                {student?.firstName} {student?.lastName}
              </div>
            </Tooltip>
          </div>
        </td>

        <td className="px-3 py-4">
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded-full text-nowrap ${getStatusClass(
              daysLeftIsNA ? 0 : daysLeft
            )}`}
          >
            {daysLeftIsNA ? (
              <>n/a</>
            ) : (
              <>
                {daysLeft} day{daysLeft === 1 ? "" : "s"}
              </>
            )}
          </span>
        </td>

        <td className="px-3 py-4">
          <button
            onClick={() => onOpenModal(student, steps.APPLY_CREDIT)}
            disabled={!isCreditButtonEnabled}
            style={{ minWidth: "115px" }}
            className="min-h-11 h-11 rounded-full font-semibold text-sm btnPrimary500"
          >
            Apply credit
          </button>
        </td>

        <td className="px-3 py-4">
          {getAutoRenewTooltip() && !canUseAutoRenewButton ? (
            <Tooltip
              title={getAutoRenewTooltip()}
              placement="top"
              arrow
              enterTouchDelay={0}
            >
              <div className="w-fit">
                <Toggle
                  isChecked={isAutoRenewOn}
                  disabled
                  onChange={onToggleAutoRenewal}
                />
              </div>
            </Tooltip>
          ) : (
            <div className="w-fit">
              <Toggle
                isChecked={isAutoRenewOn}
                onChange={onToggleAutoRenewal}
              />
            </div>
          )}
        </td>

        <td className="min-w-[192px] max-w-[192px] w-[192px] px-3 py-4">
          <Tooltip
            enterTouchDelay={0}
            title={
              isOverflowingEmail ? (
                <div className="text-white text-sm">{student?.email}</div>
              ) : (
                ""
              )
            }
            placement="top"
            arrow
          >
            <span ref={textRefEmail} className="text-xs text-nowrap">
              {student?.email}
            </span>
          </Tooltip>
        </td>

        <td className="px-3 py-4">
          <span className="text-xs px-2 py-0.5 rounded-full text-nowrap">
            {student?.sessionIds?.length}
          </span>
        </td>

        <td className="px-3 py-4">
          <div className="text-xs text-gray900 text-nowrap">
            {getStatusText(student, user)}
          </div>
        </td>
      </tr>
    </>
  );
};

export default StudentCreditsManageTableRow;
